import { checkIsModelNameAvailable } from "@/actions/db/train-voices";
import { isMatchingExtension } from "@/components/pages/custom/utils/extension-validator";
import { audio } from "@/data/extensions";

type ValidationResult = {
  isValid: boolean;
  message: string;
};

type FieldValidators = {
  [fieldName: string]: (value: any) => Promise<ValidationResult>;
};

const validators: FieldValidators = {
  image: async (value: File | null): Promise<ValidationResult> => {
    if (!value) {
      return { isValid: true, message: "" };
    }
    const allowedTypes = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(value.type)) {
      return {
        isValid: false,
        message:
          "Invalid image type. Only PNG, JPG, JPEG, and WEBP are allowed.",
      };
    }
    return { isValid: true, message: "" };
  },
  voiceName: async (value: string): Promise<ValidationResult> => {
    const validation = baseNameValidator(value);
    if (!validation.isValid) {
      return validation;
    }

    const isAvailable = await checkIsModelNameAvailable(value);
    if (!isAvailable) {
      return {
        isValid: false,
        message: "You already have a voice model with the same name.",
      };
    }

    return { isValid: true, message: "" };
  },
  voiceNameEdit: async (value: string): Promise<ValidationResult> => {
    return baseNameValidator(value);
  },
  voiceDataset: async (values: File[]): Promise<ValidationResult> => {
    if (values.length === 0) {
      return { isValid: false, message: "Voice dataset is required." };
    }

    for (const value of values) {
      if (!isMatchingExtension(value, audio)) {
        return {
          isValid: false,
          message:
            "Invalid voice dataset type. Only MP3, WAV, OGG, M4R, FLAC, AAC and OPUS are allowed.",
        };
      }
    }
    return { isValid: true, message: "" };
  },
};

const validateField = async (
  fieldName: string,
  value: any,
): Promise<ValidationResult> => {
  const validator = validators[fieldName];
  if (validator) {
    return validator(value);
  }
  return { isValid: true, message: "" };
};

const baseNameValidator = (value: string): ValidationResult => {
  if (!value) {
    return { isValid: false, message: "Please set voice name" };
  }
  if (value.length < 4) {
    return {
      isValid: false,
      message: "Voice name must be at least 4 characters long.",
    };
  }

  const regex = /^[A-Za-z0-9 ]+$/;
  if (!regex.test(value)) {
    return {
      isValid: false,
      message:
        "The name must contain only English letters and numbers, without special characters.",
    };
  }
  return { isValid: true, message: "" };
};

export default validateField;
